import { action, computed, makeObservable, observable } from 'mobx';
import { DispatcherV2Response, GroupMode, Run, SortingRequest } from '@bringg/types';
import { notifyNeedToScroll } from '@bringg-frontend/monitoring-shared';
import { getRoot } from 'mobx-easy';

import { routesMonitoringRootStore } from './routes-monitoring-root-store';
import { getInnerGroups } from '../../shared/utils/grouping';
import {
	FilterBarListItemKeys,
	RunFiltersObject,
	TaskFiltersObject
} from '../../shared/components/monitoring-filter-bar/types';
import RootStore from 'bringg-web/stores/root-store';
import { TableItem } from 'bringg-web/features/monitoring/shared/stores/groups/types';
import { monitoringFiltersProvider } from '../../shared/components/monitoring-filter-bar/utils/monitoring-filter-provider';
import { tableDefinitionSwitcher } from 'bringg-web/features/monitoring/shared/data/table-definition-switcher';
import { monitoringRootStore } from 'bringg-web/features/monitoring/shared/stores/monitoring-store';

class RoutesMonitoringStore {
	disposeSignal: AbortSignal;

	loading = false;
	refreshing: boolean;
	refreshAC: AbortController;

	hoveredRoute: number = null;

	constructor(disposeSignal: AbortSignal) {
		makeObservable(this, {
			loading: observable,
			refreshing: observable,
			hoveredRoute: observable,
			setLoading: action,
			setHoveredRoute: action,
			reset: action,
			tableDefinitionChange: action,
			setResponse: action,
			range: computed,
			routes: computed,
			groupMode: computed,
			tableItems: computed,
			currentTableDefinition: computed
		});

		this.disposeSignal = disposeSignal;
		this.refreshAC = new AbortController();

		this.disposeSignal.addEventListener(
			'abort',
			() => {
				this.refreshAC.abort();
			},
			{ once: true }
		);
	}

	public get currentTableDefinition() {
		return monitoringRootStore.getStore().tableDefinitionStore.getLatestTableDefinition('route_monitoring');
	}

	reset = () => {
		const { selectedTasksStore } = routesMonitoringRootStore.getStore();
		selectedTasksStore.clearSelectedTasks();
		routesMonitoringRootStore.reset();
		routesMonitoringRootStore.getStore().routeMonitoringMapViewStore.setTeamMarkers();
	};

	setLoading = (loading: boolean) => {
		this.loading = loading;
	};

	setHoveredRoute = (routeId: number) => {
		this.hoveredRoute = routeId;
	};

	public tableDefinitionChange(changeObj: {
		taskFilters?: TaskFiltersObject;
		runFilters?: RunFiltersObject;
		sorting?: SortingRequest;
		groupMode?: GroupMode;
	}) {
		this.reset();
		notifyNeedToScroll({
			scrollTop: 0,
			target: undefined,
			immediate: true
		});

		const firstInit = !this.currentTableDefinition;

		tableDefinitionSwitcher.switch(
			{
				...changeObj,
				filters:
					changeObj.taskFilters || changeObj.runFilters
						? { task: changeObj.taskFilters, run: changeObj.runFilters }
						: undefined,
				owner: 'route_monitoring',
				entityBaseType: 'task_group'
			},
			GroupMode.Route
		);

		if (firstInit) {
			routesMonitoringRootStore.getStore().routeMonitoringMapViewStore.listenToItemsChange();
		}
	}

	public isLoaded(flatArrayIndex: number): boolean {
		return this.currentTableDefinition.isLoaded(flatArrayIndex);
	}

	public setResponse(response: DispatcherV2Response) {
		return this.currentTableDefinition.setResponse(response, true);
	}

	get range(): {
		from: string;
		to: string;
	} {
		const date = this.currentTableDefinition.filters.run[FilterBarListItemKeys.RUN_TIME_WINDOW];
		const { startTime: from, endTime: to } = monitoringFiltersProvider.getNormalizeRunTimeWindow(date);

		return {
			from,
			to
		};
	}

	get routes(): Run[] {
		if (!this.currentTableDefinition) {
			return [];
		}
		const ids = getInnerGroups(this.currentTableDefinition.groups).map(group => group.data.id);
		return getRoot<RootStore>().data.runStore.getByIds(ids);
	}

	get groupMode(): GroupMode {
		return this.currentTableDefinition?.groupMode;
	}

	get tableItems(): TableItem[] {
		return this.currentTableDefinition?.flatTableItems;
	}
}

export default RoutesMonitoringStore;
